import type { FbAd, ListFbAdsResponse, ListFbAdsParams } from './types'
import { API_CONFIG } from '@/shared/config'

const API_URL = API_CONFIG.baseUrl

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getAuthToken } = await import('@trafiflow/shared-ui')
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }
}

export const fbAdsService = {
  /**
   * List FB ads for the current tenant
   * @param params Optional filters (brand, page_id)
   */
  async listAds(params?: ListFbAdsParams): Promise<FbAd[]> {
    const headers = await getAuthHeaders()
    
    const searchParams = new URLSearchParams()
    if (params?.brand) {
      searchParams.set('brand', params.brand)
    }
    if (params?.page_id) {
      searchParams.set('page_id', params.page_id)
    }

    const queryString = searchParams.toString()
    const url = `${API_URL}/fb-ads${queryString ? `?${queryString}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[fbAdsService] listAds error:', errorData)
      throw new Error(errorData.error || `API error: ${response.status}`)
    }

    const result: ListFbAdsResponse = await response.json()
    return result.ads || []
  },

  /**
   * Get a single FB ad by ID
   * @param adId The ad ID
   * @param pageId The page ID (required)
   */
  async getAd(adId: string, pageId: string): Promise<FbAd | null> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/fb-ads/${adId}?page_id=${encodeURIComponent(pageId)}`, {
      method: 'GET',
      headers,
    })

    if (response.status === 404) {
      return null
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[fbAdsService] getAd error:', errorData)
      throw new Error(errorData.error || `API error: ${response.status}`)
    }

    return response.json()
  },
} as const
